import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import useSnackBar from '../../../../hooks/useSnackBar/useSnackBar';

import { UemCompany } from '../../../../models/UemOnboardingModels';
import { Market } from '../../../../models/MarketsModels';
import { DropdownItem } from '../../../../components/Dropdown/DropdownModels';
import { defaultBannerTimezone, defaultTimezoneOption } from '../../../../models/BannersModels';
import { MultipleDropdownItem } from '../../../../components/MultipleDropdown/MultipleDropdownModels';

import MarketsService from '../../../../services/Markets/MarketsService';
import UemOnboardingService from '../../../../services/UemOnboarding/UemOnboardingService';

function useBannersFormContainer() {
  const [companies, setCompanies] = useState<UemCompany[]>([]);
  const [companiesOptions, setCompaniesOptions] = useState<MultipleDropdownItem[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [marketOptions, setMarketOptions] = useState<MultipleDropdownItem[]>([]);
  const [timezoneOptions, setTimezoneOptions] = useState<DropdownItem[]>([]);
  const snackBar = useSnackBar();
  const [t] = useTranslation();

  useEffect(() => {
    UemOnboardingService.api.fetchCompanies().then(
      (response) => {
        setCompanies(response.data.companies);
        setCompaniesOptions(
          response.data.companies.map((obj) => ({
            key: obj.id,
            value: obj.id,
            label: `${obj.name} ${obj.country}`,
          })),
        );
      },
      () => {
        snackBar.showSnackBar(t('pages.bannerOnboarding.fetchCompaniesError'), 'error');
      },
    );
    MarketsService.api.fetchMarkets().then(
      (response) => {
        const countryMarkets = response.data.markets.filter((market) => market.marketType === 'COUNTRY');
        setMarkets(countryMarkets);
        setMarketOptions(
          countryMarkets.map((market) => ({
            key: market.id,
            value: market.id,
            label: `${market.name} (${market.countryCode})`,
          })),
        );

        const defaultOption: DropdownItem = {
          key: defaultTimezoneOption,
          label: t('pages.bannerOnboarding.schedule.defaultTimezone'),
          value: defaultTimezoneOption,
          data: defaultBannerTimezone,
        };
        const userOption: DropdownItem = {
          key: 'user-timezone',
          label: t('pages.bannerOnboarding.schedule.yourTimezone', { offset: DateTime.now().toFormat('Z') }),
          value: 'user-timezone',
          data: DateTime.now().zoneName,
        };
        const marketTimezoneOptions = countryMarkets.map((market) => {
          const referenceTimezone = DateTime.now().setZone(market.timezone);
          return {
            key: market.id,
            label: `${market.name} (${referenceTimezone.offsetNameShort}, UTC${referenceTimezone.toFormat('Z')})`,
            value: market.id,
            data: market.timezone,
          };
        });
        setTimezoneOptions([defaultOption, userOption, ...marketTimezoneOptions]);
      },
      () => {
        snackBar.showSnackBar(t('pages.bannerOnboarding.fetchMarketsError'), 'error');
      },
    );
  }, []);

  return {
    timezoneOptions,
    companies,
    companiesOptions,
    markets,
    marketOptions,
  };
}

export default useBannersFormContainer;
